import React, { useCallback, useState } from "react";

import { useWatch } from "antd/es/form/Form";
import { FormInstance } from "antd/es/form/hooks/useForm";
import InputNumber from "antd/es/input-number";
import Popover from "antd/es/popover";
import Select from "antd/es/select";
import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { useIntl } from "react-intl";

import { Button, Dropdown, FormItem } from "@mapmycustomers/ui";

import Checkbox from "@app/component/input/Checkbox";
import IntervalUnit from "@app/enum/IntervalUnit";
import RecurringIntervalType from "@app/enum/RecurringIntervalType";
import isAnnualInterval from "@app/util/activity/isAnnualInterval";
import { SHOULD_USE_12_HOUR_FORMAT } from "@app/util/consts";
import { ActivityFieldName } from "@app/util/fieldModel/ActivityFieldModel";
import { formatDate } from "@app/util/formatters";
import useIntervalUnitOptions from "@app/util/hook/useIntervalUnitOptions";

import FormValues from "../../types/FormValues";

import styles from "./ActivityRepeat.module.scss";
import messages from "./messages";
import useActivityRepeatMenu, { UseActivityRepeatMenuOptions } from "./utils/useActivityRepeatMenu";

interface Props {
  disabled?: boolean;
  disableText?: string;
  form: FormInstance<FormValues>;
  onChange?: () => void;
}

const ActivityRepeat = ({ disabled, disableText, form, onChange }: Props) => {
  const intl = useIntl();

  const recurInterval: FormValues["recurInterval"] = form.getFieldValue("recurInterval");
  const options = useIntervalUnitOptions(recurInterval?.value ?? 1);

  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [popoverVisible, setPopoverVisible] = useState(false);

  const handleDropdownVisible = useCallback(
    (visible: boolean) => {
      if (visible) {
        setPopoverVisible(false);
        setTooltipVisible(false);
      }
      setDropdownVisible(visible);
    },
    [setDropdownVisible, setPopoverVisible, setTooltipVisible]
  );

  const handleRepeatChange = useCallback<NonNullable<UseActivityRepeatMenuOptions["onChange"]>>(
    (changed) => {
      setDropdownVisible(false);

      if (changed.recurIntervalType === RecurringIntervalType.CUSTOM) {
        setPopoverVisible(true);
      }

      onChange?.();
    },
    [onChange, setPopoverVisible]
  );

  const repeatMenu = useActivityRepeatMenu({ form, onChange: handleRepeatChange });

  const isAllDay = !!useWatch("allDay", form);

  const handleAllDayChange = useCallback(
    (checked: boolean) => {
      form.setFieldValue("allDay", checked);

      if (checked) {
        form.setFieldValue(ActivityFieldName.END_AT, undefined);
      }
      onChange?.();
    },
    [form, onChange]
  );

  const dateValue = form.getFieldValue(ActivityFieldName.START_AT);
  const isValidDate = dateValue && Date.parse(dateValue.toString());

  if (!isValidDate) {
    return null;
  }

  const dateWeekly = formatDate(dateValue, "EEEE");
  const dateMonthly = formatDate(dateValue, "do");
  const dateAnnually = formatDate(dateValue, intl.formatMessage(messages.dateFormatAnnually));
  const dateDaily = formatDate(dateValue, SHOULD_USE_12_HOUR_FORMAT ? "hh:mm aa" : "HH:mm");

  return (
    <div className={styles.container}>
      <FormItem
        noStyle
        shouldUpdate={({ isRecurrent, recurInterval, recurIntervalType }, curValues) =>
          isRecurrent !== curValues.isRecurrent ||
          recurIntervalType !== curValues.recurIntervalType ||
          recurInterval?.value !== curValues.recurInterval?.value ||
          recurInterval?.unit !== curValues.recurInterval?.unit ||
          recurInterval?.excludeWeekends !== curValues.recurInterval?.excludeWeekends
        }
      >
        {({ getFieldValue }) => {
          const recurIntervalType = getFieldValue("recurIntervalType");
          const recurInterval = getFieldValue("recurInterval");
          const isRecurrent = getFieldValue("isRecurrent");
          const recurUnit = String(recurInterval?.unit);

          let intervalDescription: string = intl.formatMessage(messages.doesNotRepeat);

          switch (recurIntervalType) {
            case RecurringIntervalType.CUSTOM:
              intervalDescription = intl.formatMessage(messages.custom);

              if (recurUnit && recurInterval?.value) {
                if (isAnnualInterval({ recurInterval, recurIntervalType })) {
                  intervalDescription = intl.formatMessage(messages.annually, {
                    date: dateAnnually,
                  });
                } else {
                  const unit = options.find((unit) => unit.key === recurUnit);
                  if (unit?.label && !isNaN(recurInterval.value)) {
                    switch (recurUnit) {
                      case IntervalUnit.HOUR:
                        intervalDescription = String(
                          intl.formatMessage(messages.every, {
                            number: recurInterval.value,
                            units: unit.label,
                          })
                        );
                        break;

                      // eslint-disable-next-line perfectionist/sort-switch-case
                      case IntervalUnit.DAY:
                        intervalDescription = String(
                          intl.formatMessage(messages.everyOn, {
                            number: recurInterval.value,
                            on: dateDaily,
                            units: unit.label,
                          })
                        );
                        break;

                      case IntervalUnit.WEEK:
                        intervalDescription = String(
                          intl.formatMessage(messages.everyOn, {
                            number: recurInterval.value,
                            on: dateWeekly,
                            units: unit.label,
                          })
                        );
                        break;

                      // eslint-disable-next-line
                      case IntervalUnit.MONTH:
                        intervalDescription = String(
                          intl.formatMessage(messages.everyOn, {
                            number: recurInterval.value,
                            on: dateMonthly,
                            units: unit.label,
                          })
                        );
                        break;

                      case IntervalUnit.YEAR:
                        intervalDescription = String(
                          intl.formatMessage(messages.everyOn, {
                            number: recurInterval.value,
                            on: dateAnnually,
                            units: unit.label,
                          })
                        );
                        break;
                    }
                  }
                }
              }

              break;
            case RecurringIntervalType.DAILY:
              intervalDescription = intl.formatMessage(messages.daily);
              break;
            case RecurringIntervalType.WEEKLY:
              intervalDescription = intl.formatMessage(messages.weekly, { date: dateWeekly });
              break;
            // eslint-disable-next-line
            case RecurringIntervalType.MONTHLY:
              intervalDescription = intl.formatMessage(messages.monthly, { date: dateMonthly });
              break;
          }

          return (
            <>
              <div className={styles.icon} />

              <Popover
                content={
                  <div className={styles.body}>
                    <div className={styles.title}>{intl.formatMessage(messages.title)}</div>
                    <div className={styles.content}>
                      <div className={styles.settings}>
                        <div className={styles.label}>
                          {intl.formatMessage(messages.repeatEvery)}
                        </div>

                        <FormItem name={["recurInterval", "value"]} noStyle>
                          <InputNumber className={styles.number} max={36} min={1} />
                        </FormItem>

                        <FormItem name={["recurInterval", "unit"]} noStyle>
                          <Select
                            className={styles.unit}
                            dropdownMatchSelectWidth
                            options={options}
                          />
                        </FormItem>
                      </div>

                      {recurUnit === IntervalUnit.DAY && (
                        <FormItem name={["recurInterval", "excludeWeekends"]} noStyle>
                          <Checkbox className={styles.checkbox}>
                            {intl.formatMessage(messages.excludeWeekends)}
                          </Checkbox>
                        </FormItem>
                      )}
                    </div>
                  </div>
                }
                onOpenChange={setPopoverVisible}
                open={popoverVisible ? popoverVisible : undefined}
                placement={"topLeft"}
                trigger={popoverVisible ? "click" : []}
              >
                <Tooltip title={disableText} trigger={disableText ? ["hover"] : []}>
                  <Dropdown
                    arrow={false}
                    disabled={disabled}
                    menu={repeatMenu}
                    onOpenChange={handleDropdownVisible}
                    open={dropdownVisible}
                    placement="topLeft"
                    trigger={["click"]}
                  >
                    <Tooltip
                      onOpenChange={setTooltipVisible}
                      open={tooltipVisible}
                      overlayClassName={styles.tooltip}
                      placement="top"
                      title={intl.formatMessage(messages.tooltip)}
                      trigger={dropdownVisible || disabled ? [] : ["hover"]}
                    >
                      <span>
                        <Button
                          className={cn(styles.btn, {
                            [styles.active]: dropdownVisible,
                            [styles.hasValue]: isRecurrent,
                          })}
                          disabled={disabled}
                          type="control"
                        >
                          {intervalDescription}
                        </Button>
                      </span>
                    </Tooltip>
                  </Dropdown>
                </Tooltip>
              </Popover>
            </>
          );
        }}
      </FormItem>
      <FormItem name={"allDay"} noStyle showLabel={false}>
        <Checkbox
          checked={isAllDay}
          className={styles.checkbox}
          disabled={disabled}
          onChange={handleAllDayChange}
        >
          {intl.formatMessage(messages.allDay)}
        </Checkbox>
      </FormItem>
    </div>
  );
};

export default ActivityRepeat;
